import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import CreatorStudioLayout from "./CreatorStudioLayout";
import { fetchCreatorDrops } from "../../services/creatorStudioService";
import {
  contentTypeLabel,
  dropEarned,
  dropProgress,
  dropViewCount,
  formatMoney,
  getDropStatus,
  timeAgo,
} from "./creatorHelpers";

const FILTERS = ["all", "open", "closed", "draft"];

export default function MyDrops() {
  const { currentUser, userProfile } = useAuth();
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!currentUser?.uid) return;
    setLoading(true);
    fetchCreatorDrops(currentUser.uid)
      .then(setDrops)
      .finally(() => setLoading(false));
  }, [currentUser?.uid]);

  const counts = useMemo(() => ({
    all: drops.length,
    open: drops.filter((d) => getDropStatus(d) === "open").length,
    closed: drops.filter((d) => getDropStatus(d) === "closed").length,
    draft: drops.filter((d) => getDropStatus(d) === "draft").length,
  }), [drops]);

  const filtered = useMemo(() => {
    let list = drops;
    if (filter !== "all") list = list.filter((d) => getDropStatus(d) === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((d) => (d.title || "").toLowerCase().includes(q));
    }
    return list;
  }, [drops, filter, search]);

  if (loading) {
    return (
      <CreatorStudioLayout>
        <div className="cs-loading">Loading drops…</div>
      </CreatorStudioLayout>
    );
  }

  return (
    <CreatorStudioLayout>
      <div className="cs-page-header">
        <div>
          <div className="cs-page-eyebrow">Creator Studio</div>
          <h1 className="cs-page-title">My <em>Drops</em></h1>
        </div>
        <Link to="/post" className="cs-btn cs-btn-primary">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Post a Drop
        </Link>
      </div>

      <div className="cs-post-cta">
        <div>
          <h3>Ready to drop something <em>new?</em></h3>
          <p>Your next drop is waiting. 100 people will see it — make it count.</p>
          <span className="cs-post-cta-hand">the window opens the moment you post</span>
        </div>
        <Link to="/post" className="cs-btn cs-btn-primary" style={{ fontSize: 14, padding: "13px 26px" }}>
          Post a Drop
        </Link>
      </div>

      <div className="cs-filter-row">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            className={`cs-filter-pill${filter === f ? " active" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)} ({counts[f]})
          </button>
        ))}
        <div className="cs-filter-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
          <input type="text" placeholder="Search drops…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="cs-drops-table">
        <div className="cs-drops-header">
          <span>Drop</span>
          <span>Status</span>
          <span>Views</span>
          <span>Earned</span>
          <span>Posted</span>
          <span />
        </div>

        {filtered.length === 0 ? (
          <div className="cs-empty">No drops match this filter.</div>
        ) : (
          filtered.map((drop) => {
            const status = getDropStatus(drop);
            const views = dropViewCount(drop);
            const left = Math.max(0, 100 - views);
            const progress = dropProgress(drop);
            const closing = status === "open" && views >= 85;
            const earned = dropEarned(drop, userProfile);
            const priceLabel = drop.isFree ? "Free" : `$${Number(drop.price || 0).toFixed(2)}`;

            return (
              <div key={drop.id} className={`cs-drop-row${status === "closed" ? " dimmed" : ""}`}>
                <div className="cs-drop-info">
                  {drop.thumbnailUrl ? (
                    <img className="cs-drop-thumb" src={drop.thumbnailUrl} alt="" />
                  ) : (
                    <div className="cs-drop-thumb-empty">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9A9088" strokeWidth="1.8" strokeLinecap="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
                      </svg>
                    </div>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div className="cs-drop-title" style={status === "draft" ? { color: "var(--text-muted)" } : undefined}>
                      {drop.title || "Untitled drop"}
                    </div>
                    <div className="cs-drop-meta">
                      {contentTypeLabel(drop.contentType)} · {priceLabel}
                    </div>
                  </div>
                </div>

                <div>
                  <span className={`cs-status-pill cs-status-${status}`}>
                    {status === "open" && "● "}
                    {status === "open" ? "Open" : status === "closed" ? "Closed" : "Draft"}
                  </span>
                </div>

                <div className="cs-prog-wrap">
                  {status === "draft" ? (
                    <>
                      <div className="cs-prog-bar"><div className="cs-prog-fill" style={{ width: 0 }} /></div>
                      <div className="cs-prog-label" style={{ color: "var(--text-muted)" }}>Not posted</div>
                    </>
                  ) : (
                    <>
                      <div className="cs-prog-bar">
                        <div className={`cs-prog-fill${closing ? " closing" : ""}${status === "closed" ? " closed" : ""}`} style={{ width: `${progress}%` }} />
                      </div>
                      <div className="cs-prog-label" style={closing ? { color: "var(--red)" } : status === "closed" ? { color: "var(--text-muted)" } : undefined}>
                        {views}/100{status === "open" ? ` · ${left} left` : " · Closed"}
                      </div>
                    </>
                  )}
                </div>

                <div>
                  <div className="cs-earn-amount" style={status === "draft" ? { color: "var(--text-muted)" } : undefined}>
                    {status === "draft" ? "—" : formatMoney(earned)}
                  </div>
                  {!drop.isFree && status !== "draft" && (
                    <div className="cs-earn-sub">{views} unlocks</div>
                  )}
                  {drop.isFree && status !== "draft" && <div className="cs-earn-sub">Free drop</div>}
                </div>

                <div className="cs-earn-sub">{timeAgo(drop.createdAt)}</div>

                {status === "draft" ? (
                  <Link to="/post" className="cs-action-btn gold">Finish & Post</Link>
                ) : (
                  <Link to={`/drop/${drop.id}`} className="cs-action-btn">
                    {status === "open" ? "Edit" : "View"}
                  </Link>
                )}
              </div>
            );
          })
        )}
      </div>
    </CreatorStudioLayout>
  );
}
